import React, { useState } from 'react';
import { Form, InputNumber, DatePicker, Upload, Button, Row, Col, message, Grid } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useSelfRegistration } from '../hooks/useSelfRegistration';
import type { UploadFile } from 'antd/es/upload/interface';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';

interface DirectDepositHandlerProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const DirectDepositHandler: React.FC<DirectDepositHandlerProps> = ({ session, onComplete, onError }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { submitDirectDepositEvidence, isSubmittingDirectDeposit } = useSelfRegistration();
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const handleFinish = async (values: any) => {
        console.log('🔄 DirectDepositHandler handleFinish called with values:', values);
        console.log('🔄 Form values:', values);
        
        // Get file from form values (which should now be populated by the Upload component)
        const formFileList = values.proofOfPayment || fileList;
        
        if (!formFileList || formFileList.length === 0) {
            console.error('❌ No file uploaded');
            onError('Please upload your proof of payment');
            return;
        }

        const uploadedFile = formFileList[0];
        const file = uploadedFile.originFileObj || uploadedFile;
        
        if (!file) {
            console.error('❌ File not found in originFileObj or direct file');
            console.log('UploadedFile structure:', uploadedFile);
            onError('File not found. Please re-upload.');
            return;
        }

        console.log('🔄 File found:', file.name, 'Size:', file.size, 'Type:', file.type);

        console.log('🔄 Submitting direct deposit evidence...');
        try {
            const updatedSession = await submitDirectDepositEvidence({
                // @ts-ignore
                sessionToken: session.sessionToken,
                amount: values.amount,
                paymentDate: values.paymentDate.format('YYYY-MM-DD'),
                file: file,
            });
            console.log('✅ Direct deposit evidence submitted successfully:', updatedSession);
            message.success('Direct deposit evidence submitted successfully for review.');
            onComplete(updatedSession);
        } catch (error: any) {
            console.error('❌ Error submitting direct deposit evidence:', error);
            onError(error.message || 'Failed to submit evidence. Please try again.');
        }
    };

    const uploadProps = {
        onRemove: () => {
            setFileList([]);
        },
        beforeUpload: (file: UploadFile) => {
            setFileList([file]);
            return false; // Prevent automatic upload
        },
        fileList,
    };

    const handleSubmitClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
            // Validate form first
            const values = await form.validateFields();
            await handleFinish(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    return (
        <div className="space-y-4 mt-4">
            <Form
                form={form}
                layout="vertical"
                className="space-y-4"
                size={isMobile ? 'middle' : 'large'}
            >
                <Row gutter={isMobile ? [8, 8] : [16, 16]}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item
                            name="amount"
                            label="Amount Deposited"
                            rules={[{ required: true, message: 'Please enter the amount you deposited' }]}
                        >
                            <InputNumber
                                style={{ 
                                    width: '100%',
                                    height: isMobile ? '44px' : '40px'
                                }}
                                placeholder="Enter amount"
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : 0}
                                size={isMobile ? 'middle' : 'large'}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item
                            name="paymentDate"
                            label="Payment Date"
                            rules={[{ required: true, message: 'Please select the payment date' }]}
                        >
                            <DatePicker 
                                style={{ 
                                    width: '100%',
                                    height: isMobile ? '44px' : '40px'
                                }}
                                size={isMobile ? 'middle' : 'large'}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="proofOfPayment"
                    label="Proof of Payment"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                        console.log('🔄 Upload event:', e);
                        if (Array.isArray(e)) {
                            return e;
                        }
                        return e && e.fileList;
                    }}
                    rules={[
                        {
                            required: true,
                            validator: (_, value) => {
                                if (!value || value.length === 0) {
                                    return Promise.reject(new Error('Please upload your proof of payment'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <Upload {...uploadProps} maxCount={1}>
                        <Button 
                            icon={<UploadOutlined />}
                            // @ts-ignore
                            size={isMobile ? 'middle' : 'default'}
                            style={{ 
                                height: isMobile ? '44px' : '32px',
                                fontSize: isMobile ? '14px' : '16px'
                            }}
                        >
                            Click to upload
                        </Button>
                    </Upload>
                </Form.Item>
            </Form>

            <Button 
                type="primary" 
                onClick={handleSubmitClick}
                loading={isSubmittingDirectDeposit} 
                block
                size={isMobile ? 'middle' : 'large'}
                style={{ 
                    height: isMobile ? '44px' : '48px',
                    fontSize: isMobile ? '16px' : '18px'
                }}
            >
                Submit for Review
            </Button>
        </div>
    );
};

export default DirectDepositHandler;
